import React, { useState } from 'react'
import {
  Button,
  Card,
  Space,
  Typography,
  Divider,
  Tag,
} from 'antd'
import {
  PlayCircleOutlined,
  ReloadOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  LoadingOutlined,
  ClockCircleOutlined,
  SwapOutlined,
  IdcardOutlined,
  NumberOutlined,
} from '@ant-design/icons'
import { profilingAPI, OcrDocument } from '../services/api'
import LLMNarrativeCard, { LLMNarrative } from './LLMNarrativeCard'

const { Text, Title } = Typography

// ── Types ─────────────────────────────────────────────────────────────────────

type PhaseStatus = 'idle' | 'loading' | 'success' | 'error' | 'skipped'

interface SubStep {
  label: string
  status: PhaseStatus
  detail?: string
}

interface StepState {
  status: PhaseStatus
  subSteps: SubStep[]
  result?: any
}

type LookupMode = 'simulation_id' | 'cedula' | null

interface AutomatedFlowProps {
  simulationId: string
  documents?: OcrDocument[]   // alternative to simulationId
  cedula?: string             // optional CC for cross-validation in doc mode
  onComplete?: (results: any) => void
}

// ── Helpers ───────────────────────────────────────────────────────────────────

const isSeasNotFound = (err: any): boolean => {
  const code = err?.response?.data?.code || err?.response?.data?.detail?.code
  if (code === 'SEAS_NOT_FOUND') return true
  const status = err?.response?.status
  // SEAS errors come back as 404 with SEAS_NOT_FOUND code; also accept raw 5xx
  if (status === 500 || status === 502 || status === 503) return true
  return false
}

const stepIcon = (status: PhaseStatus) => {
  switch (status) {
    case 'loading':  return <LoadingOutlined style={{ color: '#1890ff' }} />
    case 'success':  return <CheckCircleOutlined style={{ color: '#52c41a' }} />
    case 'error':    return <CloseCircleOutlined style={{ color: '#ff4d4f' }} />
    case 'skipped':  return <SwapOutlined style={{ color: '#faad14' }} />
    default:         return <ClockCircleOutlined style={{ color: '#d9d9d9' }} />
  }
}

const stepColor = (status: PhaseStatus): string => {
  switch (status) {
    case 'loading':  return '#1890ff'
    case 'success':  return '#52c41a'
    case 'error':    return '#ff4d4f'
    case 'skipped':  return '#faad14'
    default:         return '#8c8c8c'
  }
}

// ── Component ─────────────────────────────────────────────────────────────────

const AutomatedFlow: React.FC<AutomatedFlowProps> = ({ simulationId, documents, cedula, onComplete }) => {
  const [running, setRunning] = useState(false)
  const [lookupMode, setLookupMode] = useState<LookupMode>(null)
  const [narrative, setNarrative] = useState<{ data: LLMNarrative; decision: string } | null>(null)
  const [steps, setSteps] = useState<StepState[]>([
    { status: 'idle', subSteps: [] },  // 0 Trigger
    { status: 'idle', subSteps: [] },  // 1 Análisis SEAS
    { status: 'idle', subSteps: [] },  // 2 Resultado
    { status: 'idle', subSteps: [] },  // 3 Estado
  ])

  const updateStep = (index: number, patch: Partial<StepState>) =>
    setSteps(prev => prev.map((s, i) => i === index ? { ...s, ...patch } : s))

  const setSubSteps = (index: number, subSteps: SubStep[]) =>
    updateStep(index, { subSteps })

  const resetFlow = () => {
    setSteps([
      { status: 'idle', subSteps: [] },
      { status: 'idle', subSteps: [] },
      { status: 'idle', subSteps: [] },
      { status: 'idle', subSteps: [] },
    ])
    setLookupMode(null)
    setNarrative(null)
  }

  const isDocMode = !!(documents && documents.length > 0)

  const runFlow = async () => {
    if (!isDocMode && !simulationId.trim()) return
    setRunning(true)
    resetFlow()

    const allResults: any = {}
    let profilingId: string | null = null

    try {

      if (isDocMode) {
        // ── MODO DOCUMENTOS ──────────────────────────────────────────────────

        // Paso 1: omitido (analyze_from_documents crea su propio registro)
        updateStep(0, {
          status: 'skipped',
          subSteps: [{ label: 'Omitido en modo documentos — el análisis crea el registro directamente', status: 'skipped' }],
        })

        await new Promise(r => setTimeout(r, 200))

        // Paso 2: OCR + análisis
        updateStep(1, { status: 'loading', subSteps: [
          { label: 'Ejecutando OCR sobre los documentos…', status: 'loading' },
        ]})

        try {
          const analyzeResult = await profilingAPI.analyzeWithDocs({ documents: documents!, cedula })
          allResults.analyze = analyzeResult
          profilingId = analyzeResult.profiling_id

          const narrativeDetails = analyzeResult.validations?.llm_narrative?.details
          if (narrativeDetails && typeof narrativeDetails === 'object') {
            setNarrative({ data: narrativeDetails as LLMNarrative, decision: analyzeResult.decision || '' })
          }

          updateStep(1, {
            status: 'success',
            result: analyzeResult,
            subSteps: [
              { label: 'Identidad e ingresos extraídos del OCR', status: 'success' },
              { label: `Decisión: ${analyzeResult.decision || 'N/A'}`, status: 'success' },
              { label: 'Nota: verificación de antecedentes no disponible sin ID SEAS', status: 'skipped' },
              ...(narrativeDetails ? [{ label: 'Narrativo IA generado', status: 'success' as const }] : []),
            ],
          })
        } catch (err: any) {
          const msg = err?.response?.data?.detail?.message
            || err?.response?.data?.detail
            || err?.message
            || 'Error desconocido'
          updateStep(1, {
            status: 'error',
            subSteps: [{ label: `Error en análisis OCR: ${msg}`, status: 'error' }],
          })
          throw new Error(msg)
        }

      } else {
        // ── MODO ID (flujo original) ─────────────────────────────────────────

        // ── Paso 1: Trigger ──────────────────────────────────────────────────
        updateStep(0, { status: 'loading', subSteps: [{ label: 'Registrando solicitud en el sistema…', status: 'loading' }] })
        try {
          const triggerResult = await profilingAPI.trigger({
            simulation_id: simulationId,
            trigger_source: 'frontend_automated',
            priority: 'normal',
          })
          allResults.trigger = triggerResult
          profilingId = triggerResult.profiling_id
          updateStep(0, {
            status: 'success',
            result: triggerResult,
            subSteps: [{ label: `Solicitud registrada — ID: ${triggerResult.profiling_id}`, status: 'success' }],
          })
        } catch (err: any) {
          updateStep(0, {
            status: 'error',
            subSteps: [{ label: `Error al registrar: ${err?.response?.data?.detail || err?.message}`, status: 'error' }],
          })
        }

        await new Promise(r => setTimeout(r, 300))

        // ── Paso 2: Análisis SEAS ────────────────────────────────────────────
        updateStep(1, { status: 'loading', subSteps: [
          { label: `Consultando SEAS con Simulation ID "${simulationId}"…`, status: 'loading' },
        ]})

        let analyzeResult: any = null

        try {
          analyzeResult = await profilingAPI.analyze({
            simulation_id: simulationId,
            customer_type: 'pensionado',
            force_refresh: false,
          })
          setLookupMode('simulation_id')
          allResults.analyze = analyzeResult
          profilingId = analyzeResult.profiling_id || profilingId

          const narrativeDetails1 = analyzeResult.validations?.llm_narrative?.details
          if (narrativeDetails1 && typeof narrativeDetails1 === 'object') {
            setNarrative({ data: narrativeDetails1 as LLMNarrative, decision: analyzeResult.decision || '' })
          }

          updateStep(1, {
            status: 'success',
            result: analyzeResult,
            subSteps: [
              { label: `Simulation ID encontrado en SEAS`, status: 'success' },
              { label: `Decisión: ${analyzeResult.decision || 'N/A'}`, status: 'success' },
              ...(narrativeDetails1 ? [{ label: 'Narrativo IA generado', status: 'success' as const }] : []),
            ],
          })
        } catch (firstErr: any) {
          if (isSeasNotFound(firstErr)) {
            setSubSteps(1, [
              { label: `Simulation ID "${simulationId}" no encontrado en SEAS`, status: 'error' },
              { label: `Reintentando como número de cédula…`, status: 'loading' },
            ])

            await new Promise(r => setTimeout(r, 400))

            try {
              analyzeResult = await profilingAPI.analyze({
                simulation_id: simulationId,
                cedula: simulationId,
                customer_type: 'pensionado',
                force_refresh: false,
              })
              setLookupMode('cedula')
              allResults.analyze = analyzeResult
              profilingId = analyzeResult.profiling_id || profilingId

              const narrativeDetails2 = analyzeResult.validations?.llm_narrative?.details
              if (narrativeDetails2 && typeof narrativeDetails2 === 'object') {
                setNarrative({ data: narrativeDetails2 as LLMNarrative, decision: analyzeResult.decision || '' })
              }

              updateStep(1, {
                status: 'success',
                result: analyzeResult,
                subSteps: [
                  { label: `Simulation ID "${simulationId}" no encontrado en SEAS`, status: 'error' },
                  { label: `Datos encontrados por Cédula`, status: 'success' },
                  { label: `Decisión: ${analyzeResult.decision || 'N/A'}`, status: 'success' },
                  ...(narrativeDetails2 ? [{ label: 'Narrativo IA generado', status: 'success' as const }] : []),
                ],
              })
            } catch (secondErr: any) {
              setLookupMode(null)
              const msg = secondErr?.response?.data?.detail?.message
                || secondErr?.response?.data?.detail
                || secondErr?.message
                || 'Error desconocido'
              updateStep(1, {
                status: 'error',
                subSteps: [
                  { label: `Simulation ID "${simulationId}" no encontrado en SEAS`, status: 'error' },
                  { label: `Tampoco encontrado como Cédula: ${msg}`, status: 'error' },
                ],
              })
              throw new Error(`Ambos métodos fallaron: ${msg}`)
            }
          } else {
            const msg = firstErr?.response?.data?.detail?.message
              || firstErr?.response?.data?.detail
              || firstErr?.message
              || 'Error desconocido'
            updateStep(1, {
              status: 'error',
              subSteps: [{ label: `Error de análisis: ${msg}`, status: 'error' }],
            })
            throw new Error(msg)
          }
        }

      } // end mode branch

      await new Promise(r => setTimeout(r, 300))

      // ── Paso 3: Resultado ────────────────────────────────────────────────
      updateStep(2, { status: 'loading', subSteps: [{ label: 'Obteniendo resultado final…', status: 'loading' }] })
      if (profilingId) {
        try {
          const result = await profilingAPI.getResult(profilingId)
          allResults.result = result
          updateStep(2, {
            status: 'success',
            result,
            subSteps: [{ label: `Estado: ${result.decision || 'N/A'}`, status: 'success' }],
          })
        } catch {
          updateStep(2, {
            status: 'skipped',
            subSteps: [{ label: 'Resultado aún procesándose — reintenta en unos segundos', status: 'skipped' }],
          })
        }
      } else {
        updateStep(2, { status: 'skipped', subSteps: [{ label: 'Sin profiling_id — omitido', status: 'skipped' }] })
      }

      await new Promise(r => setTimeout(r, 300))

      // ── Paso 4: Estado ───────────────────────────────────────────────────
      updateStep(3, { status: 'loading', subSteps: [{ label: 'Verificando estado…', status: 'loading' }] })
      if (profilingId) {
        try {
          const status = await profilingAPI.getStatus(profilingId)
          allResults.status = status
          updateStep(3, {
            status: 'success',
            result: status,
            subSteps: [{ label: `Progreso: ${status.progress_percentage ?? 100}%`, status: 'success' }],
          })
        } catch {
          updateStep(3, {
            status: 'skipped',
            subSteps: [{ label: 'Estado no disponible', status: 'skipped' }],
          })
        }
      } else {
        updateStep(3, { status: 'skipped', subSteps: [{ label: 'Sin profiling_id — omitido', status: 'skipped' }] })
      }

      onComplete?.(allResults)

    } catch {
      // Already reflected in step state
    } finally {
      setRunning(false)
    }
  }

  const stepTitles = ['Registrar solicitud', 'Análisis SEAS', 'Obtener resultado', 'Verificar estado']
  const hasStarted = steps.some(s => s.status !== 'idle')

  return (
    <Card
      title="Flujo Automatizado de Testing"
      extra={
        <Space>
          <Button
            type="primary"
            icon={<PlayCircleOutlined />}
            loading={running}
            onClick={runFlow}
            disabled={isDocMode ? !documents?.length : !simulationId}
          >
            Ejecutar Flujo
          </Button>
          <Button icon={<ReloadOutlined />} onClick={resetFlow} disabled={running}>
            Reset
          </Button>
        </Space>
      }
    >
      <Space direction="vertical" style={{ width: '100%' }} size="middle">

        {/* Fuente + modo de búsqueda */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          {isDocMode ? (
            <>
              <Tag icon={<IdcardOutlined />} color="green">Modo documentos OCR</Tag>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {documents!.length} archivo{documents!.length !== 1 ? 's' : ''} cargado{documents!.length !== 1 ? 's' : ''}
              </Text>
            </>
          ) : (
            <>
              <Text strong>ID: </Text>
              <Text code style={{ fontSize: 15 }}>{simulationId || '—'}</Text>
              {lookupMode === 'simulation_id' && (
                <Tag icon={<NumberOutlined />} color="blue">Encontrado como Simulation ID</Tag>
              )}
              {lookupMode === 'cedula' && (
                <Tag icon={<IdcardOutlined />} color="purple">Encontrado como Cédula (fallback)</Tag>
              )}
            </>
          )}
        </div>

        {/* Steps */}
        {stepTitles.map((title, i) => (
          <div key={i}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
              <div style={{ marginTop: 2 }}>{stepIcon(steps[i].status)}</div>
              <div style={{ flex: 1 }}>
                <Text
                  strong
                  style={{ color: hasStarted ? stepColor(steps[i].status) : '#595959' }}
                >
                  {`Paso ${i + 1}: ${title}`}
                </Text>
                {steps[i].subSteps.length > 0 && (
                  <div style={{ marginTop: 4, paddingLeft: 4 }}>
                    {steps[i].subSteps.map((sub, j) => (
                      <div key={j} style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 2 }}>
                        <span style={{ fontSize: 11 }}>{stepIcon(sub.status)}</span>
                        <Text
                          style={{
                            fontSize: 12,
                            color: sub.status === 'loading' ? '#1890ff'
                              : sub.status === 'error' ? '#ff4d4f'
                              : sub.status === 'skipped' ? '#faad14'
                              : '#595959',
                          }}
                        >
                          {sub.label}
                        </Text>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            {i < stepTitles.length - 1 && (
              <div style={{ marginLeft: 10, borderLeft: '1px dashed #d9d9d9', height: 8 }} />
            )}
          </div>
        ))}

        {/* Narrativo LLM */}
        {narrative && !running && (
          <>
            <Divider style={{ margin: '4px 0' }} />
            <LLMNarrativeCard narrative={narrative.data} decision={narrative.decision} />
          </>
        )}

        {/* JSON colapsable */}
        {hasStarted && !running && (
          <>
            <Divider style={{ margin: '4px 0' }} />
            <details>
              <summary style={{ cursor: 'pointer', color: '#1890ff', fontSize: 13 }}>
                Ver resultados en JSON
              </summary>
              <pre style={{
                marginTop: 8,
                maxHeight: 360,
                overflow: 'auto',
                background: '#f6f8fa',
                padding: 12,
                borderRadius: 4,
                fontSize: 12,
              }}>
                {JSON.stringify(steps.map((s, i) => ({
                  step: stepTitles[i],
                  status: s.status,
                  result: s.result,
                })), null, 2)}
              </pre>
            </details>
          </>
        )}

      </Space>
    </Card>
  )
}

export default AutomatedFlow
